import {useState} from 'react'
import {useSelector} from 'react-redux'
import {ModalBase} from '../../../../components/Modals/ModalBase'
import SearchSelect from '../../../../components/Inputs/SearchSelect'
import {State} from '../../../../reducers'
import {DocumentoRelacionadoFE} from '../../../../interfaces/factura-exportacion.interface'
import {getDateOperacionMes} from '../../../../utils/stringDate'
import {
  LONGITUD_MAXIMA_NUMERO_DOCUMENTO_RELACIONADO,
  TIPOS_GENERACION_DOCUMENTO_RELACIONADO,
  TIPOS_DTE_RELACIONADO_EXPORTACION,
} from './documentos-relacionados.utils'

interface ErroresDocumentoRelacionado {
  tipoDocumento: string
  tipoGeneracion: string
  numeroDocumento: string
  fechaEmision: string
}

const SIN_ERRORES: ErroresDocumentoRelacionado = {
  tipoDocumento: '',
  tipoGeneracion: '',
  numeroDocumento: '',
  fechaEmision: '',
}

interface PropiedadesModalDocumentoRelacionadoFEX {
  documentoEdit?: DocumentoRelacionadoFE | null
  onClicCerrar: () => void
  onClicGuardar: (documento: DocumentoRelacionadoFE) => void
}

export default function ModalDocumentoRelacionadoFEX({
  documentoEdit,
  onClicCerrar,
  onClicGuardar,
}: PropiedadesModalDocumentoRelacionadoFEX) {
  const {tipoDocumentosEmision} = useSelector((state: State) => state.catalogos)

  const [form, setForm] = useState<DocumentoRelacionadoFE>(
    documentoEdit ?? {
      id: 0,
      tipoDocumento: '',
      tipoDocumentoDescripcion: '',
      numeroDocumento: '',
      fechaEmision: new Date().toLocaleDateString('en-CA'),
      tipoGeneracion: 2,
    }
  )
  const [errores, setErrores] = useState<ErroresDocumentoRelacionado>(SIN_ERRORES)

  const tiposDocumento = (tipoDocumentosEmision ?? []).filter((documento: any) =>
    TIPOS_DTE_RELACIONADO_EXPORTACION.includes(`${documento?.codigodocumento ?? ''}`)
  )

  const descripcionDelTipo = (codigo: string): string =>
    (tipoDocumentosEmision ?? []).find(
      (documento: any) => `${documento?.codigodocumento ?? ''}` === codigo
    )?.nombre ?? ''

  const cambiarTipoDocumento = (codigo: string) => {
    setForm({...form, tipoDocumento: codigo, tipoDocumentoDescripcion: descripcionDelTipo(codigo)})
    setErrores({...errores, tipoDocumento: ''})
  }

  const cambiarNumeroDocumento = (valor: string) => {
    setForm({...form, numeroDocumento: valor})
    setErrores({...errores, numeroDocumento: ''})
  }

  const cambiarFechaEmision = (valor: string) => {
    setForm({...form, fechaEmision: valor})
    setErrores({...errores, fechaEmision: ''})
  }

  const cambiarTipoGeneracion = (valor: string) => {
    setForm({...form, tipoGeneracion: Number(valor) || 0})
    setErrores({...errores, tipoGeneracion: ''})
  }

  const guardar = (evento: any) => {
    evento.preventDefault()

    const encontrados: ErroresDocumentoRelacionado = {
      tipoDocumento: form.tipoDocumento ? '' : 'Seleccione el tipo de documento relacionado.',
      tipoGeneracion: form.tipoGeneracion ? '' : 'Seleccione el tipo de generación.',
      numeroDocumento: form.numeroDocumento.trim()
        ? ''
        : 'Escriba el número del documento relacionado.',
      fechaEmision: form.fechaEmision ? '' : 'Seleccione la fecha de emisión.',
    }

    setErrores(encontrados)

    if (Object.values(encontrados).some((error) => error !== '')) return

    onClicGuardar({
      ...form,
      numeroDocumento: form.numeroDocumento.trim(),
      tipoDocumentoDescripcion:
        form.tipoDocumentoDescripcion || descripcionDelTipo(form.tipoDocumento),
    })
  }

  return (
    <ModalBase Styles={{minWidth: '40%'}}>
      <div className='w-layout-cell'>
        <div className='card w-form'>
          <form className='form-content' onSubmit={guardar}>
            <div>
              <h3 className='card-title l'>Documento relacionado</h3>
              <p className='m'>
                En la normativa 2.0 la factura de exportación puede declarar los documentos que la
                originan. Registre cada uno con su tipo, su número y la fecha en que se emitió.
              </p>
              <div className='form-separator'></div>
            </div>
            <div className='w-layout-hflex h-inputs'>
              <div className='label-input'>
                <label htmlFor='tipoDocumento' className='field-label m'>
                  Tipo de documento<span style={{color: 'red'}}>*</span>
                </label>
                <SearchSelect
                  defaultValue={null}
                  data={tiposDocumento}
                  keyNameText='nombre'
                  keyNameValue='codigodocumento'
                  placeholder='Tipo de documento'
                  name='tipoDocumento'
                  disabled={false}
                  placement='bottom'
                  value={form.tipoDocumento}
                  onChange={(opcion: any) => cambiarTipoDocumento(`${opcion?.value ?? ''}`)}
                />
                {errores.tipoDocumento && <p className='alert s'>{errores.tipoDocumento}</p>}
              </div>
              <div className='label-input'>
                <label htmlFor='tipoGeneracion' className='field-label m'>
                  Tipo de generación<span style={{color: 'red'}}>*</span>
                </label>
                <SearchSelect
                  defaultValue={null}
                  data={TIPOS_GENERACION_DOCUMENTO_RELACIONADO}
                  keyNameText='valor'
                  keyNameValue='codigo'
                  placeholder='Tipo de generación'
                  name='tipoGeneracion'
                  disabled={false}
                  placement='bottom'
                  value={`${form.tipoGeneracion}`}
                  onChange={(opcion: any) => cambiarTipoGeneracion(`${opcion?.value ?? ''}`)}
                />
                {errores.tipoGeneracion && <p className='alert s'>{errores.tipoGeneracion}</p>}
              </div>
            </div>
            <div className='w-layout-hflex h-inputs'>
              <div className='label-input'>
                <label htmlFor='numeroDocumento' className='field-label m'>
                  Número de documento<span style={{color: 'red'}}>*</span>
                </label>
                <input
                  autoFocus
                  autoComplete='off'
                  type='text'
                  className='m text-field w-input'
                  name='numeroDocumento'
                  data-name='numeroDocumento'
                  placeholder='Número del documento relacionado'
                  id='numeroDocumento'
                  maxLength={LONGITUD_MAXIMA_NUMERO_DOCUMENTO_RELACIONADO}
                  value={form.numeroDocumento}
                  onChange={(evento) => cambiarNumeroDocumento(evento.target.value)}
                />
                {errores.numeroDocumento && <p className='alert s'>{errores.numeroDocumento}</p>}
              </div>
              <div className='label-input'>
                <label htmlFor='fechaEmision' className='field-label m'>
                  Fecha de emisión<span style={{color: 'red'}}>*</span>
                </label>
                <input
                  autoComplete='off'
                  type='date'
                  className='m text-field w-input'
                  name='fechaEmision'
                  data-name='fechaEmision'
                  id='fechaEmision'
                  min={getDateOperacionMes(-(12 * 10))}
                  max={getDateOperacionMes(0)}
                  value={form.fechaEmision}
                  onChange={(evento) => cambiarFechaEmision(evento.target.value)}
                />
                {errores.fechaEmision && <p className='alert s'>{errores.fechaEmision}</p>}
              </div>
            </div>
            <div className='w-layout-hflex button-actions' style={{marginTop: '8px'}}>
              <button
                type='button'
                className='m button transparent w-inline-block'
                onClick={onClicCerrar}
              >
                <div>Cancelar</div>
              </button>
              <button type='submit' className='m button w-inline-block'>
                <div>{documentoEdit ? 'Actualizar' : 'Agregar'}</div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalBase>
  )
}
